/**
 * RE-PLAN A ROLLOVER CHAIN'S UNRESOLVED FUTURE — the pure half.
 *
 * Every path that changes when an existing chain's remaining work can happen
 * (a pushback on the part being worked, a change to its work) asks the same
 * question: given the frozen history, the pool of future work and the earliest
 * valid start, what should the unresolved tail look like? This module answers
 * it with ONE run of the shared planner and turns the answer into the payload
 * `replan_rollover_chain_from_source` persists in one transaction — existing
 * tail rows reused in order, extra days inserted, surplus days retired.
 *
 * No I/O. Deno and the browser both import it.
 */

import {
  planRolloverSegments,
  segmentBookMinutes,
  type ResolveWorkDay,
  type RolloverSegment as PlannedSegment,
} from './rolloverSegmentPlanner.ts';
import { makeShopWindowInstants } from './timezoneMath.ts';

/** The chain-row fields a re-plan reads. */
export interface ReplanChainRow {
  id: string;
  status: string | null;
  deleted_at?: string | null;
  actually_started?: boolean | null;
  timer_status?: string | null;
  rollover_type?: string | null;
  predicted_minutes: number | null;
  opti_time?: number | null;
  scheduled_date: string | null;
  start_time: string | null;
  opti_finish?: string | null;
  assigned_tech_id: string | null;
  sequence_index?: number | null;
  rollover_of_ticket_id?: string | null;
}

/** Live, never started, no timer: the only rows a re-plan may move. */
export function isUnresolvedChainRow(r: ReplanChainRow): boolean {
  return !r.deleted_at && (r.status === 'Active' || r.status === 'Paused')
    && !r.actually_started && r.timer_status !== 'running' && r.timer_status !== 'paused';
}

/** A row that is gone (deleted, cancelled, superseded) — not part of the lineage any more. */
function isTombstoned(r: ReplanChainRow): boolean {
  return !!r.deleted_at || r.status === 'Deleted' || r.status === 'Cancelled';
}

const chainKey = (r: ReplanChainRow) =>
  `${r.scheduled_date ?? ''}|${String(r.sequence_index ?? 0).padStart(6, '0')}|${r.start_time ?? ''}`;

/**
 * THE UNRESOLVED FUTURE of `sourceId`: the rows that continue its work, found
 * by lineage (`rollover_of_ticket_id` — each part names the part it follows),
 * up to the first one that is not unresolved. A started, paused, running or
 * finished continuation is history: the tail stops there, and nothing after
 * it is re-planned across it. Tombstoned rows are not part of the lineage.
 * Split siblings that merely share the chain root are not the source's work
 * and are never included.
 */
export function unresolvedLineageTail(sourceId: string, rows: ReplanChainRow[]): ReplanChainRow[] {
  const tail: ReplanChainRow[] = [];
  const seen = new Set<string>([sourceId]);
  let current = sourceId;
  for (;;) {
    const next = rows
      .filter((r) => r.rollover_of_ticket_id === current && !seen.has(r.id) && !isTombstoned(r))
      .sort((a, b) => (chainKey(a) < chainKey(b) ? -1 : 1))[0];
    if (!next || !isUnresolvedChainRow(next)) break;
    tail.push(next);
    seen.add(next.id);
    current = next.id;
  }
  return tail;
}

/**
 * Does something still-live follow the tail's last row (or the source, for an
 * empty tail)? Then the tail is BOUNDED by history — a started, running or
 * finished continuation that names that row as its predecessor — and a
 * re-plan may reuse or retire the tail's rows but never add one: a new day
 * would fork the lineage around a row whose history cannot be relinked.
 */
export function tailIsBoundedByHistory(sourceId: string, tail: ReplanChainRow[], rows: ReplanChainRow[]): boolean {
  const last = tail.length ? tail[tail.length - 1].id : sourceId;
  return rows.some((r) => r.rollover_of_ticket_id === last && !isTombstoned(r) && !isUnresolvedChainRow(r));
}

/**
 * THE CHAIN'S LINEAGE from its head: the head, then each live row that names
 * the previous one as its predecessor (tombstones skipped; on a legacy fan the
 * earliest by date/generation/start wins). This is the chain's one order.
 */
export function lineageFromHead(headId: string, rows: ReplanChainRow[]): ReplanChainRow[] {
  const head = rows.find((r) => r.id === headId && !isTombstoned(r));
  if (!head) return [];
  const out: ReplanChainRow[] = [head];
  const seen = new Set<string>([head.id]);
  for (;;) {
    const cur = out[out.length - 1].id;
    const next = rows
      .filter((r) => r.rollover_of_ticket_id === cur && !seen.has(r.id) && !isTombstoned(r))
      .sort((a, b) => (chainKey(a) < chainKey(b) ? -1 : 1))[0];
    if (!next) break;
    out.push(next);
    seen.add(next.id);
  }
  return out;
}

/** One chain re-plan, ready for `replan_rollover_chain_from_source`. */
export interface ChainReplanPayload {
  segments: PlannedSegment[];
  updates: Array<Record<string, unknown>>;
  inserts: Array<Record<string, unknown>>;
  retire: string[];
}

export type ChainReplanResult =
  | ({ ok: true } & ChainReplanPayload)
  | { ok: false; reason: 'NO_WORKDAY_IN_HORIZON' | 'NO_TEMPLATE'; unplacedMinutes: number };

/**
 * Plan the pool once and lay it onto the tail.
 *
 * Units. A PLANNED chain carries its work in book minutes: `tailBookMinutes`
 * is split exactly across the new days (non-final parts floor, the final part
 * takes the remainder) and each day's OptiTime is the planner's work minutes.
 * A LIVE chain (converted rows: 1-minute shadows grown by durable
 * accumulation) carries its planned work in `opti_time`: `tailBookMinutes` is
 * null, shadows are left exactly as they are, and a new day starts a fresh
 * 1-minute shadow like every converted continuation.
 *
 * The tail follows `predecessorId` — the history row it continues — or, when
 * that is null, the tail IS the head of the chain (an unstarted root moving as
 * the first day of its own plan). An OFF-SCHEDULE chain (on the backlog: no
 * technician, no OptiFinish) keeps that shape: it is re-dated and re-shared,
 * but gets no finish and no OptiTime until someone schedules it.
 */
export function buildChainReplan(input: {
  predecessorId: string | null;
  /** The predecessor's generation; the first tail day is `seqBase + 1`. */
  seqBase: number;
  tail: ReplanChainRow[];
  /** Row cloned for extra days when the tail has none of its own. */
  templateId?: string;
  /** The technician the days are assigned to; null keeps the chain unassigned. */
  techId: string | null;
  /** The pool, in the technician's OptiTime (wall work minutes). */
  poolWorkMinutes: number;
  /** Planned chains: the pool's book minutes, conserved exactly. Live: null. */
  tailBookMinutes: number | null;
  efficiencyPct: number;
  bufferPct: number;
  startDateStr: string;
  startMin: number;
  resolveDay: ResolveWorkDay;
  timezone: string;
  /** Backlog chain: dates and shares only — no OptiFinish, no OptiTime. */
  offSchedule?: boolean;
  /** Unique suffix for the ids of inserted days. */
  idStamp: string | number;
}): ChainReplanResult {
  const { predecessorId, tail, techId, timezone } = input;
  const plan = planRolloverSegments({
    totalWorkMinutes: input.poolWorkMinutes,
    startDateStr: input.startDateStr,
    startMin: input.startMin,
    resolveDay: input.resolveDay,
  });
  if (!plan.complete) {
    return { ok: false, reason: 'NO_WORKDAY_IN_HORIZON', unplacedMinutes: plan.unplacedMinutes };
  }
  const segs = plan.segments;
  const book = input.tailBookMinutes === null || segs.length === 0
    ? null
    : segmentBookMinutes(segs.map((s) => s.workMinutes), input.tailBookMinutes, input.efficiencyPct, input.bufferPct);

  const template = tail.length > 0 ? tail[tail.length - 1].id : (input.templateId ?? predecessorId);
  const idRoot = predecessorId ?? tail[0]?.id ?? template ?? 'chain';
  const ids = segs.map((_, k) => (k < tail.length ? tail[k].id : `${idRoot}-RPLN-${input.idStamp}-${k + 2}`));
  // Core-100 R4 wave 2 (CORE-AC Rel, DST): the segment's start is its true instant and its finish is
  // the start plus the window's elapsed minutes (R4-22's windowFinishInstant rule) — never the finish's
  // wall minute re-read on its own.
  const win = (d: string, s: number, f: number) => makeShopWindowInstants(d, s, f, timezone);

  const updates: Array<Record<string, unknown>> = [];
  const inserts: Array<Record<string, unknown>> = [];
  segs.forEach((sg, k) => {
    const w = win(sg.dateStr, sg.startMin, sg.finishMin);
    const row: Record<string, unknown> = {
      id: ids[k],
      assigned_tech_id: techId,
      scheduled_date: sg.dateStr,
      start_time: w.start.toISOString(),
      opti_finish: input.offSchedule ? null : w.finish.toISOString(),
      opti_time: input.offSchedule ? null : sg.workMinutes,
      sequence_index: input.seqBase + k + 1,
      rollover_of_ticket_id: k === 0 ? predecessorId : ids[k - 1],
    };
    if (k < tail.length) {
      if (book) row.predicted_minutes = book[k];
      updates.push(row);
    } else {
      if (!template) return;
      row.predicted_minutes = book ? book[k] : 1;
      row.template_id = template;
      inserts.push(row);
    }
  });
  if (inserts.length + updates.length !== segs.length) {
    return { ok: false, reason: 'NO_TEMPLATE', unplacedMinutes: 0 };
  }
  const retire = tail.slice(segs.length).map((r) => r.id);
  return { ok: true, segments: segs, updates, inserts, retire };
}

/**
 * Is the tail already what the plan says? Then persisting it would change
 * nothing — and a caller that runs every minute (the pushback tick) must not
 * write. Same rows, same technician, same days, same OptiTime, same book
 * shares, same lineage, no row starting EARLIER than its planned start, and a
 * row that starts on plan also finishing on plan. A
 * row the lane cascade has placed LATER on the same day (behind other work) is
 * the board's decision, not a stale plan — rewriting it every tick would fight
 * the cascade forever.
 */
export function chainReplanIsNoop(tail: ReplanChainRow[], payload: ChainReplanPayload): boolean {
  if (payload.inserts.length > 0 || payload.retire.length > 0) return false;
  if (payload.updates.length !== tail.length) return false;
  return payload.updates.every((u, k) => {
    const r = tail[k];
    if (r.id !== u.id) return false;
    if (r.assigned_tech_id !== u.assigned_tech_id) return false;
    if ((r.scheduled_date ?? '').slice(0, 10) !== u.scheduled_date) return false;
    if ((r.opti_time ?? null) !== u.opti_time) return false;
    if ('predicted_minutes' in u && r.predicted_minutes !== u.predicted_minutes) return false;
    if ((r.sequence_index ?? null) !== u.sequence_index) return false;
    if ((r.rollover_of_ticket_id ?? null) !== u.rollover_of_ticket_id) return false;
    if (!r.start_time) return false;
    const start = Date.parse(r.start_time);
    const plannedStart = Date.parse(String(u.start_time));
    if (start < plannedStart) return false;
    // Where it starts on plan, it must also end on plan.
    if (start === plannedStart) {
      if (u.opti_finish === null) return !r.opti_finish;
      return !!r.opti_finish && Date.parse(r.opti_finish) === Date.parse(String(u.opti_finish));
    }
    return true;
  });
}

/**
 * NOV 3 (founder Decisions 2 + 3) — LOST TIME MOVES WORK, IT NEVER CREATES WORK.
 *
 * A started chain row (the source) was planned to do `share` book minutes of the repair's approved labor on its own
 * day, in `optiTime` wall minutes. Pause, break, parts, overnight and run-long time are not labor: they never add a
 * minute to the repair. What they can do is leave part of the source's share UNDONE when its day can no longer hold
 * it. Only that part moves to the chain's unresolved future, and the source keeps the rest — the chain's total is
 * conserved exactly (Σ shares before = Σ shares after), so no growth path can raise booked work above the approved
 * labor (FD-2026-10-28-C/-D, FD-2026-10-24-B).
 *
 *   consumed  = the share's part already done: recorded hands-on work at the row's book-per-wall rate (the factors
 *               its expectation was frozen at; or share / optiTime), never more than the share — running long is not
 *               progress the plan can bank;
 *   plannable = what the source's day can still hold: productive wall minutes left on ITS day (0 once that day has
 *               ended — so an overnight or never-closed pause stops moving anything), at the same rate;
 *   keep      = min(share, consumed + plannable), at least 1 (a started row keeps a non-zero expectation);
 *   moved     = share − keep.
 *
 * A source that ran long (worked ≥ planned) moves nothing: its overrun is counted once, in its own finish and the lane
 * placement behind it — never again as book minutes on the tail. Pure; no I/O.
 */
export function lostTimeShareMove(input: {
  /** The source's authority share (book minutes). */
  share: number | null | undefined;
  /** The source's planned OptiTime (wall minutes) for that share; null/0 → the technician's factors. */
  optiTime: number | null | undefined;
  /** Recorded hands-on work on the source (finishIntentDerivation.workedMinutes). */
  workedMinutes: number;
  /** Productive wall minutes still available on the source's own day (0 when that day is over). */
  capacityLeftWall: number;
  efficiencyPct: number;
  bufferPct: number;
  /**
   * While the source's day still has capacity, move only in steps of at least this many book minutes (each move
   * appends an audited revision; a per-minute claim must not write one per minute). At the end of the day
   * (capacityLeftWall 0) the exact remainder always moves. Default 0.
   */
  minMidDayStep?: number;
}): { keep: number; moved: number; consumed: number; plannable: number } {
  const share = Math.max(0, Math.floor(Number(input.share ?? 0)));
  if (share <= 1) return { keep: share, moved: 0, consumed: 0, plannable: 0 };
  const opti = Number(input.optiTime ?? 0);
  const rate = Number.isFinite(opti) && opti > 0
    ? share / opti
    : (Math.max(input.efficiencyPct || 100, 1) / 100) / (1 + Math.max(0, input.bufferPct || 0) / 100);
  const EPS = 1e-9;
  const worked = Math.max(0, Number.isFinite(input.workedMinutes) ? input.workedMinutes : 0);
  const left = Math.max(0, Number.isFinite(input.capacityLeftWall) ? input.capacityLeftWall : 0);
  const consumed = Math.min(share, Math.floor(worked * rate + EPS));
  const plannable = Math.floor(left * rate + EPS);
  const keep = Math.max(1, Math.min(share, consumed + plannable));
  if (left > 0 && share - keep < Math.max(0, input.minMidDayStep ?? 0)) return { keep: share, moved: 0, consumed, plannable };
  return { keep, moved: share - keep, consumed, plannable };
}
